import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, Table, TableHead, TableBody, TableRow, TableCell,
  Chip, TextField, MenuItem, Button, CircularProgress, FormControlLabel, Switch, Tooltip,
  Tabs, Tab, LinearProgress, ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import {
  Refresh, HighlightOff, Block, CallSplit, Security, MonitorHeart, CheckCircle,
  Insights, Speed, Download, Replay, CallMissedOutgoing, TrendingUp, WarningAmber, InfoOutlined,
} from '@mui/icons-material';
import api from '../../services/api';
import { COUNTRY_LIST } from '../../utils/dialCountry';

const TYPE_META: any = {
  auth_reject:      { label: 'Auth Reject', color: 'warning', icon: <Block fontSize="small" /> },
  call_failed:      { label: 'Call Failed', color: 'error',   icon: <HighlightOff fontSize="small" /> },
  routing_failover: { label: 'Failover',    color: 'info',    icon: <CallSplit fontSize="small" /> },
  fraud:            { label: 'Fraud',        color: 'error',   icon: <Security fontSize="small" /> },
  passed:           { label: 'Connected',    color: 'success', icon: <CheckCircle fontSize="small" /> },
};

const ago = (ts: string) => {
  if (!ts) return '—';
  const s = Math.floor((Date.now() - new Date(ts).getTime()) / 1000);
  if (s < 0) return 'now';
  if (s < 60) return s + 's ago';
  if (s < 3600) return Math.floor(s / 60) + 'm ago';
  if (s < 86400) return Math.floor(s / 3600) + 'h ago';
  return Math.floor(s / 86400) + 'd ago';
};

const mmss = (sec?: number) => {
  const t = Math.max(0, Math.round(Number(sec) || 0));
  return Math.floor(t / 60) + ':' + String(t % 60).padStart(2, '0');
};

const codeColor = (c: number) =>
  !c ? '#888' : c === 200 ? '#2e7d32' : c === 503 ? '#c62828' : c >= 500 ? '#ad1457'
    : c >= 400 ? '#ed6c02' : '#6366F1';

// Shared "why ASR moved" banner — red if the drop is capacity/carrier (actionable),
// blue if it's down but for normal call reasons, green if ASR improving.
const AsrExplainerBanner: React.FC<{ exp: any }> = ({ exp }) => {
  if (!exp || !exp.headline) return null;
  const st = exp.direction === 'up' ? { bg: 'rgba(46,125,50,0.08)', bar: '#2e7d32', Icon: TrendingUp }
    : (exp.nature === 'capacity' || exp.nature === 'carrier_error') ? { bg: 'rgba(198,40,40,0.07)', bar: '#c62828', Icon: WarningAmber }
    : exp.direction === 'down' ? { bg: 'rgba(2,119,189,0.07)', bar: '#0277bd', Icon: InfoOutlined }
    : { bg: 'rgba(0,0,0,0.03)', bar: '#78909c', Icon: InfoOutlined };
  const Icon = st.Icon;
  return (
    <Box sx={{ mb: 2, p: 1.5, borderRadius: 1, bgcolor: st.bg, borderLeft: `4px solid ${st.bar}`,
               display: 'flex', gap: 1.2, alignItems: 'flex-start' }}>
      <Icon sx={{ color: st.bar, mt: 0.2 }} />
      <Box>
        <Typography variant="body2" fontWeight={700} sx={{ color: st.bar }}>{exp.headline}</Typography>
        {exp.nature_phrase && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.3 }}>{exp.nature_phrase}</Typography>}
        {exp.causes && exp.causes.length > 0 && (
          <Box sx={{ mt: 0.8, display: 'flex', gap: 0.7, flexWrap: 'wrap' }}>
            {exp.causes.slice(0, 5).map((c: any) => (
              <Chip key={c.key} size="small" variant="outlined"
                color={c.nature === 'capacity' || c.nature === 'carrier_error' ? 'error' : c.nature === 'policy' ? 'warning' : 'default'}
                label={`${c.label}: ${c.n.toLocaleString()} (${c.pct_of_failed}% of fails)`} />
            ))}
          </Box>
        )}
        {exp.source === 'cdr' && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            Based on CDRs. A 503/congestion drop rarely creates a CDR — check the <b>SIP-Truth Analytics</b> tab for capacity detail.
          </Typography>
        )}
      </Box>
    </Box>
  );
};

// ───────────────────────── Tab 1: existing Service Health ──────────────────
// A second ASR the operator can re-base by excluding failure reasons that aren't
// the carrier's fault (caller cancelled, busy, no-answer...). It drives off the
// SAME per-reason counts the explainer banner shows (asr_explainer.causes), so
// the numbers always match. Deselecting a reason drops its calls from the
// attempted denominator, so the ratio reflects only the causes still selected.
// Purely client-side; the headline ASR card above is left untouched.
const SelectedCriteriaAsr: React.FC<{ exp: any; answered: number }> = ({ exp, answered }) => {
  const causes: any[] = (exp && exp.causes) || [];
  const [excluded, setExcluded] = useState<Set<string>>(new Set());
  if (!causes.length) return null;
  const ans = Number(answered || 0);
  const selFail = causes.reduce((a, c) => a + (excluded.has(c.key) ? 0 : Number(c.n || 0)), 0);
  const attempted = ans + selFail;
  const asr = attempted > 0 ? (100 * ans / attempted) : 0;
  return (
    <Card variant="outlined" sx={{ borderLeft: '4px solid #6366F1', mb: 1 }}>
      <CardContent sx={{ py: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="h4" fontWeight={800} sx={{ color: '#6366F1' }}>{asr.toFixed(1)}%</Typography>
          <Typography variant="subtitle2" fontWeight={700}>ASR by Selected Criteria</Typography>
          <Typography variant="caption" color="text.secondary">
            answered {ans.toLocaleString()} / attempted {attempted.toLocaleString()} — click a reason to exclude it from the denominator
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
          {causes.map(c => {
            const on = !excluded.has(c.key);
            return (
              <Chip key={c.key} size="small"
                label={`${c.label}: ${Number(c.n || 0).toLocaleString()}`}
                color={on ? 'default' : 'success'}
                variant={on ? 'filled' : 'outlined'}
                onClick={() => setExcluded(prev => { const nx = new Set(prev); if (on) nx.add(c.key); else nx.delete(c.key); return nx; })}
                sx={{ textDecoration: on ? 'none' : 'line-through', opacity: on ? 1 : 0.75 }}
              />
            );
          })}
        </Box>
      </CardContent>
    </Card>
  );
};

const ServiceHealthPanel: React.FC = () => {
  const [data, setData] = useState<any>({ summary: {}, events: [], window_minutes: 60 });
  const [loading, setLoading] = useState(true);
  const [win, setWin] = useState('60');
  const [type, setType] = useState('all');
  const [auto, setAuto] = useState(true);
  const [tenants, setTenants] = useState<any[]>([]);
  const [custFilter, setCustFilter] = useState('');
  const [countryFilter, setCountryFilter] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const timer = useRef<any>(null);

  useEffect(() => {
    api.get('/tenants?limit=500&tier=customer')
      .then(({ data }) => setTenants(data.data || data || []))
      .catch(() => {});
  }, []);

  // A fixed past range doesn't change — pause auto-refresh while one is active.
  useEffect(() => { if (from && to) setAuto(false); }, [from, to]);

  const load = useCallback(() => {
    const p = new URLSearchParams({ window: win, type, limit: '300' });
    if (custFilter) p.set('tenant_id', custFilter);
    if (countryFilter) p.set('country', countryFilter);
    if (from && to) { p.set('from', new Date(from).toISOString()); p.set('to', new Date(to).toISOString()); }
    api.get('/calls/failures?' + p.toString())
      .then(({ data }) => setData(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [win, type, custFilter, countryFilter, from, to]);

  useEffect(() => { setLoading(true); load(); }, [load]);
  useEffect(() => {
    if (!auto) return;
    timer.current = setInterval(load, 8000);
    return () => clearInterval(timer.current);
  }, [auto, load]);

  const s = data.summary || {};
  const events: any[] = data.events || [];

  const health = [
    { label: 'Total Calls', val: s.calls_total ?? 0,     color: '#334155' },
    { label: 'Connected',   val: s.calls_connected ?? 0, color: '#2e7d32' },
    { label: 'Failed / Unanswered', val: s.calls_failed ?? 0, color: '#d32f2f' },
    { label: 'ASR', val: (s.asr ?? 0) + '%',             color: '#6366F1' },
  ];
  const cards = [
    { key: 'auth_reject',      label: 'Auth Rejects (pre-CDR)', val: s.auth_reject,      color: '#ed6c02', hint: 'Calls rejected BEFORE a CDR — bad IP / not authorized / blocked / no rate / no balance.' },
    { key: 'call_failed',      label: 'Failed Calls',           val: s.call_failed,      color: '#d32f2f', hint: 'CDRs that failed with a 4xx/5xx/6xx response (excludes 487 caller-cancel).' },
    { key: 'routing_failover', label: 'Route Failovers',        val: s.routing_failover, color: '#0288d1', hint: 'Primary gateway failed; call rerouted to a failover carrier.' },
    { key: 'fraud',            label: 'Fraud Alerts',           val: s.fraud,            color: '#d32f2f', hint: 'Fraud-rule triggers in this window.' },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
        <TextField select size="small" label="Window" value={from && to ? '' : win} disabled={!!(from && to)} onChange={e => setWin(e.target.value)} sx={{ width: 110 }}>
          <MenuItem value="15">15 min</MenuItem>
          <MenuItem value="60">1 hour</MenuItem>
          <MenuItem value="360">6 hours</MenuItem>
          <MenuItem value="1440">24 hours</MenuItem>
        </TextField>
        <TextField size="small" type="datetime-local" label="From" InputLabelProps={{ shrink: true }} value={from} onChange={e => setFrom(e.target.value)} sx={{ width: 200 }} />
        <TextField size="small" type="datetime-local" label="To" InputLabelProps={{ shrink: true }} value={to} onChange={e => setTo(e.target.value)} sx={{ width: 200 }} />
        {(from || to) && <Button size="small" onClick={() => { setFrom(''); setTo(''); setAuto(true); }}>Live</Button>}
        <TextField select size="small" label="View" value={type} onChange={e => setType(e.target.value)} sx={{ width: 160 }}>
          <MenuItem value="all">All failures</MenuItem>
          <MenuItem value="passed">Passed (connected)</MenuItem>
          <MenuItem value="auth_reject">Auth Rejects</MenuItem>
          <MenuItem value="call_failed">Failed Calls</MenuItem>
          <MenuItem value="routing_failover">Failovers</MenuItem>
          <MenuItem value="fraud">Fraud</MenuItem>
        </TextField>
        <TextField select size="small" label="Customer" value={custFilter} onChange={e => setCustFilter(e.target.value)} sx={{ width: 170 }}>
          <MenuItem value="">All customers</MenuItem>
          {tenants.map((t: any) => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Country" value={countryFilter} onChange={e => setCountryFilter(e.target.value)} sx={{ width: 170 }}>
          <MenuItem value="">All countries</MenuItem>
          {COUNTRY_LIST.map((c) => <MenuItem key={c.code} value={c.code}>{c.name}</MenuItem>)}
        </TextField>
        <FormControlLabel control={<Switch checked={auto} disabled={!!(from && to)} onChange={e => setAuto(e.target.checked)} />} label="Auto" />
        <Button variant="outlined" startIcon={<Refresh />} onClick={load}>Refresh</Button>
      </Box>

      {/* ASR explainer — why ASR (CDR-based) is up/down: capacity vs normal failures */}
      <AsrExplainerBanner exp={data.asr_explainer} />

      <SelectedCriteriaAsr exp={data.asr_explainer} answered={Number(s.calls_connected ?? 0)} />

      <Grid container spacing={2} sx={{ mb: 1 }}>
        {health.map(c => (
          <Grid item xs={6} md={3} key={c.label}>
            <Card variant="outlined" sx={{ borderLeft: `4px solid ${c.color}` }}>
              <CardContent sx={{ py: 1.5 }}>
                <Typography variant="h4" fontWeight={800} sx={{ color: c.color }}>{c.val}</Typography>
                <Typography variant="body2" color="text.secondary">{c.label}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={2} sx={{ mb: 1 }}>
        {cards.map(c => (
          <Grid item xs={6} md={3} key={c.key}>
            <Tooltip title={c.hint} arrow>
              <Card variant="outlined" sx={{ cursor: 'help', borderLeft: `4px solid ${c.color}` }}>
                <CardContent sx={{ py: 1.5 }}>
                  <Typography variant="h4" fontWeight={800} sx={{ color: c.color }}>{c.val ?? 0}</Typography>
                  <Typography variant="body2" color="text.secondary">{c.label}</Typography>
                </CardContent>
              </Card>
            </Tooltip>
          </Grid>
        ))}
      </Grid>

      <Typography variant="caption" color="text.secondary">
        Active now: <b>{s.active_now ?? 0}</b> · Last CDR: <b>{ago(s.last_cdr)}</b> · window {data.window_minutes} min · showing {events.length} rows
      </Typography>

      <Card variant="outlined" sx={{ mt: 1 }}>
        {loading ? <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress /></Box> : (
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {['Time', 'Type', 'Source / IP', 'Destination', 'Reason', 'Code', 'Sent by', 'Customer'].map(h => (
                    <TableCell key={h} sx={{ fontWeight: 700 }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {events.map((e: any, i: number) => {
                  const m = TYPE_META[e.type] || { label: e.type, color: 'default' };
                  return (
                    <TableRow key={i} hover>
                      <TableCell><Tooltip title={new Date(e.event_time).toLocaleString()}><span>{ago(e.event_time)}</span></Tooltip></TableCell>
                      <TableCell><Chip size="small" icon={m.icon} label={m.label} color={m.color} variant="outlined" /></TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                        {e.src || '—'}{e.src_ip ? <div style={{ color: '#888' }}>{e.src_ip}</div> : null}
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{e.dst || '—'}</TableCell>
                      <TableCell>{e.reason || '—'}{e.detail ? <div style={{ color: '#888', fontSize: '0.78rem' }}>{e.detail}</div> : null}</TableCell>
                      <TableCell>{e.code || '—'}</TableCell>
                      <TableCell><SentBy by={e.sent_by} why={e.sent_reason} /></TableCell>
                      <TableCell>{e.tenant_name || '—'}</TableCell>
                    </TableRow>
                  );
                })}
                {events.length === 0 && (
                  <TableRow><TableCell colSpan={8} align="center" sx={{ py: 5, color: 'text.secondary' }}>
                    {type === 'passed' ? 'No connected calls in this window.' : 'No failures in this window 🎉'}
                  </TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </Box>
        )}
      </Card>
    </Box>
  );
};

// Who generated a failure response: a carrier (relayed, e.g. BT), our SBC, or the customer (cancel).
const SentBy: React.FC<{ by?: string; why?: string }> = ({ by, why }) => {
  if (!by) return <>—</>;
  const color: any = by === 'Our SBC' ? 'primary' : by === 'Customer' ? 'default' : 'warning';
  return (
    <Tooltip title={why || ''}>
      <span>
        <Chip size="small" variant="outlined" color={color} label={by} />
        {why ? <div style={{ color: '#888', fontSize: '0.72rem', maxWidth: 240 }}>{why}</div> : null}
      </span>
    </Tooltip>
  );
};

// ───────────────── Live CPS-per-customer widget (ingress attempt rate) ─────
const LiveCpsWidget: React.FC = () => {
  const [cps, setCps] = useState<any>({ total_cps: 0, customers: [] });
  const [on, setOn] = useState(true);
  const t = useRef<any>(null);
  const poll = useCallback(() => {
    api.get('/calls/live-cps?seconds=10').then(({ data }) => setCps(data)).catch(() => {});
  }, []);
  useEffect(() => {
    poll();
    if (!on) return;
    t.current = setInterval(poll, 3000);
    return () => clearInterval(t.current);
  }, [on, poll]);
  const custs: any[] = cps.customers || [];
  const maxCps = Math.max(1, ...custs.map(c => c.cps));
  return (
    <Card variant="outlined" sx={{ borderTop: '3px solid #6366F1', height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Speed sx={{ color: '#6366F1' }} />
          <Typography variant="subtitle1" fontWeight={700}>Live Ingress CPS · by customer</Typography>
          <Chip size="small" color="primary" label={`${cps.total_cps ?? 0} cps total`} sx={{ ml: 'auto', fontWeight: 700 }} />
          <FormControlLabel sx={{ ml: 0 }} control={<Switch size="small" checked={on} onChange={e => setOn(e.target.checked)} />} label="Live" />
        </Box>
        <Typography variant="caption" color="text.secondary">
          Real attempt-rate from raw SIP (10 s rolling) — includes calls rejected before any CDR.
        </Typography>
        <Box sx={{ mt: 1.5 }}>
          {custs.length === 0 && <Typography variant="body2" color="text.secondary">No live ingress traffic right now.</Typography>}
          {custs.map((c, i) => (
            <Box key={i} sx={{ mb: 1.2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
                <Typography variant="body2" fontWeight={600} sx={{ fontFamily: c.customer?.match(/^\d/) ? 'monospace' : 'inherit' }}>{c.customer}</Typography>
                <Typography variant="body2"><b>{c.cps}</b> cps <span style={{ color: '#888' }}>· peak {c.peak_cps}/s</span></Typography>
              </Box>
              <LinearProgress variant="determinate" value={Math.min(100, (c.cps / maxCps) * 100)}
                sx={{ height: 7, borderRadius: 4 }} />
            </Box>
          ))}
        </Box>
      </CardContent>
    </Card>
  );
};

// ───────────────────────── Tab 2: SIP-truth analytics ─────────────────────
const SIP_CODES = [200, 403, 404, 408, 480, 486, 487, 500, 502, 503, 504, 603];
const WINDOWS = [
  { v: '60', l: '1h' }, { v: '120', l: '2h' }, { v: '180', l: '3h' },
  { v: '240', l: '4h' }, { v: '480', l: '8h' }, { v: '1440', l: '24h' },
];

const SipTruthPanel: React.FC = () => {
  const [data, setData] = useState<any>({ summary: {}, by_customer: [], by_code: [], by_carrier: [], recent: [] });
  const [loading, setLoading] = useState(true);
  const [win, setWin] = useState('60');
  const [code, setCode] = useState('');
  const [scenario, setScenario] = useState('');
  const [customer, setCustomer] = useState('');
  const [country, setCountry] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [auto, setAuto] = useState(true);
  const timer = useRef<any>(null);

  // A fixed past range doesn't change — pause auto-refresh while one is active.
  useEffect(() => { if (from && to) setAuto(false); }, [from, to]);

  const params = useCallback(() => {
    const p = new URLSearchParams({ window: win });
    if (code) p.set('code', code);
    if (scenario) p.set('scenario', scenario);
    if (customer) p.set('customer', customer);
    if (country) p.set('country', country);
    if (from && to) { p.set('from', new Date(from).toISOString()); p.set('to', new Date(to).toISOString()); }
    return p;
  }, [win, code, scenario, customer, country, from, to]);

  // Never stack polls: skip a tick while the previous request is still running. A custom
  // range can take minutes to compute, so give it a long timeout (the server caches it).
  const inFlight = useRef(false);
  const load = useCallback(() => {
    if (inFlight.current) return;
    inFlight.current = true;
    const isRange = !!(from && to);
    api.get('/calls/sip-stats?' + params().toString(), { timeout: isRange ? 300000 : 30000 })
      .then(({ data }) => setData(data)).catch(() => {})
      .finally(() => { inFlight.current = false; setLoading(false); });
  }, [params, from, to]);

  useEffect(() => { setLoading(true); load(); }, [load]);
  useEffect(() => {
    if (!auto) return;
    timer.current = setInterval(load, 10000);
    return () => clearInterval(timer.current);
  }, [auto, load]);

  const download = async () => {
    try {
      const res = await api.get('/calls/sip-export?' + params().toString(), { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = `sip-failures-${win}m${scenario ? '-' + scenario : ''}${code ? '-' + code : ''}${country ? '-cc' + country : ''}.csv`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch { /* ignore */ }
  };

  const s = data.summary || {};
  const cards = [
    { label: 'Total (SIP)', val: s.total ?? 0, color: '#334155', hint: 'Every call attempt seen at SIP level — even those that never became a CDR.' },
    { label: 'Answered', val: s.answered ?? 0, color: '#2e7d32', hint: '200 OK on any route.' },
    { label: 'ASR', val: (s.asr ?? 0) + '%', color: '#6366F1', hint: 'Answer-seizure ratio.' },
    { label: 'ACD', val: mmss(s.acd_sec), color: '#0277bd', hint: `Average call duration of answered calls (from CDRs): ${s.acd_sec ?? 0}s · ${s.acd_minutes ?? 0} min avg over ${s.answered_cdrs ?? 0} answered calls.` },
    { label: 'Avg attempts', val: s.avg_attempts ?? 0, color: '#455a64', hint: 'Average egress route attempts per call (1 = no failover).' },
    { label: 'Failed 1st route', val: s.failed_first ?? 0, color: '#ed6c02', hint: 'First carrier returned a 4xx/5xx (may still have recovered on failover).', filter: () => setScenario('failed_first') },
    { label: 'Failed BOTH routes', val: s.failed_both ?? 0, color: '#c62828', hint: 'Primary AND failover both failed — customer got a hard failure.', filter: () => setScenario('failed_both') },
    { label: '503-then-Recovered', val: s.recovered_503 ?? 0, color: '#00897b', hint: 'First attempt failed (e.g. 503) but a later route answered — the retry saved the call.', filter: () => setScenario('recovered') },
    { label: '503 seen', val: s.c503 ?? 0, color: '#ad1457', hint: 'Calls where a 503 Service Unavailable appeared on any route.', filter: () => setCode('503') },
    { label: '500 seen', val: s.c500 ?? 0, color: '#8e24aa', hint: 'Calls where a 500 appeared on any route.', filter: () => setCode('500') },
    { label: '404 seen', val: s.c404 ?? 0, color: '#ef6c00', hint: 'Number not found on any route.', filter: () => setCode('404') },
    { label: '408 seen', val: s.c408 ?? 0, color: '#f9a825', hint: 'Timeout — carrier did not respond.', filter: () => setCode('408') },
    { label: 'Failed (any)', val: s.failed ?? 0, color: '#d32f2f', hint: 'Calls not answered on any route.' },
  ];
  const custs: any[] = data.by_customer || [];
  const carriers: any[] = data.by_carrier || [];
  const codes: any[] = data.by_code || [];
  const recent: any[] = data.recent || [];
  const senders: any[] = data.by_sender || [];
  const codeTotal = codes.reduce((n, c) => n + c.n, 0) || 1;

  return (
    <Box>
      {/* controls */}
      <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <ToggleButtonGroup size="small" exclusive value={from && to ? null : win} disabled={!!(from && to)} onChange={(_, v) => v && setWin(v)}>
          {WINDOWS.map(w => <ToggleButton key={w.v} value={w.v} sx={{ px: 1.5, fontWeight: 700 }}>{w.l}</ToggleButton>)}
        </ToggleButtonGroup>
        <TextField size="small" type="datetime-local" label="From" InputLabelProps={{ shrink: true }} value={from} onChange={e => setFrom(e.target.value)} sx={{ width: 200 }} />
        <TextField size="small" type="datetime-local" label="To" InputLabelProps={{ shrink: true }} value={to} onChange={e => setTo(e.target.value)} sx={{ width: 200 }} />
        {(from || to) && <Button size="small" onClick={() => { setFrom(''); setTo(''); setAuto(true); }}>Live</Button>}
        <TextField select size="small" label="SIP code" value={code} onChange={e => setCode(e.target.value)} sx={{ width: 120 }}>
          <MenuItem value="">All codes</MenuItem>
          {SIP_CODES.map(c => <MenuItem key={c} value={String(c)}>{c}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Scenario" value={scenario} onChange={e => setScenario(e.target.value)} sx={{ width: 190 }}>
          <MenuItem value="">Any outcome</MenuItem>
          <MenuItem value="failed_first">Failed 1st route</MenuItem>
          <MenuItem value="failed_both">Failed BOTH routes</MenuItem>
          <MenuItem value="recovered">Recovered after 503</MenuItem>
        </TextField>
        <TextField select size="small" label="Customer" value={customer} onChange={e => setCustomer(e.target.value)} sx={{ width: 190 }}>
          <MenuItem value="">All customers</MenuItem>
          {custs.map((c, i) => <MenuItem key={i} value={c.customer}>{c.customer}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Country" value={country} onChange={e => setCountry(e.target.value)} sx={{ width: 170 }}>
          <MenuItem value="">All countries</MenuItem>
          {COUNTRY_LIST.map((c) => <MenuItem key={c.code} value={c.code}>{c.name}</MenuItem>)}
        </TextField>
        {(code || scenario || customer || country) &&
          <Button size="small" onClick={() => { setCode(''); setScenario(''); setCustomer(''); setCountry(''); }}>Clear</Button>}
        <Box sx={{ ml: 'auto', display: 'flex', gap: 1, alignItems: 'center' }}>
          <FormControlLabel control={<Switch checked={auto} disabled={!!(from && to)} onChange={e => setAuto(e.target.checked)} />} label="Auto" />
          <Button variant="outlined" startIcon={<Refresh />} onClick={load}>Refresh</Button>
          <Button variant="contained" startIcon={<Download />} onClick={download}>Export CDRs</Button>
        </Box>
      </Box>

      {loading && <LinearProgress sx={{ mb: 1 }} />}

      {/* ASR explainer — why ASR is up/down and whether it's capacity or normal failures */}
      <AsrExplainerBanner exp={data.asr_explainer} />

      {/* summary cards */}
      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        {cards.map(c => (
          <Grid item xs={6} sm={4} md={2} key={c.label}>
            <Tooltip title={c.hint} arrow>
              <Card variant="outlined"
                onClick={c.filter}
                sx={{ borderLeft: `4px solid ${c.color}`, cursor: c.filter ? 'pointer' : 'help', height: '100%',
                      '&:hover': c.filter ? { boxShadow: 3 } : {} }}>
                <CardContent sx={{ py: 1.3, '&:last-child': { pb: 1.3 } }}>
                  <Typography variant="h5" fontWeight={800} sx={{ color: c.color }}>{c.val}</Typography>
                  <Typography variant="caption" color="text.secondary">{c.label}</Typography>
                </CardContent>
              </Card>
            </Tooltip>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={2}>
        {/* live CPS */}
        <Grid item xs={12} md={4}><LiveCpsWidget /></Grid>

        {/* code distribution */}
        <Grid item xs={12} md={4}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Insights sx={{ color: '#6366F1' }} />
                <Typography variant="subtitle1" fontWeight={700}>Delivered code distribution</Typography>
              </Box>
              {codes.length === 0 && <Typography variant="body2" color="text.secondary">No data.</Typography>}
              {codes.slice(0, 10).map((c, i) => (
                <Box key={i} sx={{ mb: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
                    <Chip size="small" label={c.code || '—'} sx={{ bgcolor: codeColor(c.code), color: '#fff', fontWeight: 700, height: 20 }} />
                    <Typography variant="body2"><b>{c.n}</b> <span style={{ color: '#888' }}>· {((c.n / codeTotal) * 100).toFixed(1)}%</span></Typography>
                  </Box>
                  <LinearProgress variant="determinate" value={(c.n / codeTotal) * 100}
                    sx={{ height: 6, borderRadius: 4, '& .MuiLinearProgress-bar': { bgcolor: codeColor(c.code) } }} />
                  {(c.by_carrier || c.by_sbc || c.by_customer) ? (
                    <Typography variant="caption" color="text.secondary">
                      {[c.by_carrier ? `carrier ${c.by_carrier}` : '', c.by_sbc ? `our SBC ${c.by_sbc}` : '',
                        c.by_customer ? `customer ${c.by_customer}` : ''].filter(Boolean).join(' · ')}
                    </Typography>
                  ) : null}
                </Box>
              ))}
            </CardContent>
          </Card>
        </Grid>

        {/* first-route carrier health */}
        <Grid item xs={12} md={4}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <CallMissedOutgoing sx={{ color: '#c62828' }} />
                <Typography variant="subtitle1" fontWeight={700}>First-route carrier health</Typography>
              </Box>
              <Table size="small">
                <TableHead><TableRow>
                  {['Carrier', 'Attempts', 'OK', '5xx', '503'].map(h => <TableCell key={h} sx={{ fontWeight: 700, px: 1 }}>{h}</TableCell>)}
                </TableRow></TableHead>
                <TableBody>
                  {carriers.map((c, i) => (
                    <TableRow key={i} hover>
                      <TableCell sx={{ px: 1, fontWeight: 600 }}>{c.carrier}</TableCell>
                      <TableCell sx={{ px: 1 }}>{c.attempts}</TableCell>
                      <TableCell sx={{ px: 1, color: '#2e7d32' }}>{c.ok}</TableCell>
                      <TableCell sx={{ px: 1, color: '#ad1457' }}>{c.c5xx}</TableCell>
                      <TableCell sx={{ px: 1, color: '#c62828', fontWeight: 700 }}>{c.c503}</TableCell>
                    </TableRow>
                  ))}
                  {carriers.length === 0 && <TableRow><TableCell colSpan={5} align="center" sx={{ py: 3, color: 'text.secondary' }}>No data.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* per-customer table */}
      <Card variant="outlined" sx={{ mt: 2 }}>
        <CardContent sx={{ pb: 0 }}>
          <Typography variant="subtitle1" fontWeight={700}>Per-customer SIP outcomes</Typography>
        </CardContent>
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead><TableRow>
              {['Customer', 'Calls', 'Answered', 'ASR', 'Failed both', 'Recovered (503)', '503 seen'].map(h =>
                <TableCell key={h} sx={{ fontWeight: 700 }}>{h}</TableCell>)}
            </TableRow></TableHead>
            <TableBody>
              {custs.map((c, i) => (
                <TableRow key={i} hover sx={{ cursor: 'pointer' }} onClick={() => setCustomer(c.customer)}>
                  <TableCell sx={{ fontWeight: 600 }}>{c.customer}</TableCell>
                  <TableCell>{c.calls}</TableCell>
                  <TableCell sx={{ color: '#2e7d32' }}>{c.answered}</TableCell>
                  <TableCell><b>{c.asr}%</b></TableCell>
                  <TableCell sx={{ color: '#c62828', fontWeight: 700 }}>{c.failed_both}</TableCell>
                  <TableCell sx={{ color: '#00897b', fontWeight: 700 }}>{c.recovered_503}</TableCell>
                  <TableCell sx={{ color: '#ad1457' }}>{c.c503}</TableCell>
                </TableRow>
              ))}
              {custs.length === 0 && <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>No calls in this window.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </Box>
      </Card>

      {/* who sent the failures */}
      <Card variant="outlined" sx={{ mt: 2 }}>
        <CardContent sx={{ pb: 0 }}>
          <Typography variant="subtitle1" fontWeight={700}>Failures by sender</Typography>
          <Typography variant="caption" color="text.secondary">
            Who generated the failure the customer received: a carrier (relayed as-is, with the carrier's own Reason / origin), our SBC (local reject, timeout), or the customer (cancelled).
          </Typography>
        </CardContent>
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead><TableRow>
              {['Sent by', 'Code', 'Reason', 'Calls'].map(h => <TableCell key={h} sx={{ fontWeight: 700 }}>{h}</TableCell>)}
            </TableRow></TableHead>
            <TableBody>
              {senders.map((r, i) => (
                <TableRow key={i} hover>
                  <TableCell><SentBy by={r.final_by} /></TableCell>
                  <TableCell><b style={{ color: codeColor(r.code) }}>{r.code}</b></TableCell>
                  <TableCell>{r.reason || '—'}</TableCell>
                  <TableCell><b>{r.n}</b></TableCell>
                </TableRow>
              ))}
              {senders.length === 0 && <TableRow><TableCell colSpan={4} align="center" sx={{ py: 3, color: 'text.secondary' }}>No failures attributed in this window yet.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </Box>
      </Card>

      {/* recent calls (per-route detail) */}
      <Card variant="outlined" sx={{ mt: 2 }}>
        <CardContent sx={{ pb: 0 }}>
          <Typography variant="subtitle1" fontWeight={700}>Recent calls · per-route SIP detail</Typography>
          <Typography variant="caption" color="text.secondary">Newest 200 matching the filters. Reconstructed from raw SIP — includes calls with no CDR.</Typography>
        </CardContent>
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead><TableRow>
              {['Time', 'Customer', 'Caller', 'Callee', 'Try', '1st route', '2nd route', 'Delivered', 'Sent by', 'Outcome'].map(h =>
                <TableCell key={h} sx={{ fontWeight: 700 }}>{h}</TableCell>)}
            </TableRow></TableHead>
            <TableBody>
              {recent.map((r, i) => (
                <TableRow key={i} hover>
                  <TableCell><Tooltip title={new Date(r.first_time).toLocaleString()}><span>{ago(r.first_time)}</span></Tooltip></TableCell>
                  <TableCell>{r.customer}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>{r.caller || '—'}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>{r.callee || '—'}</TableCell>
                  <TableCell>{r.attempts}</TableCell>
                  <TableCell>{r.first_carrier ? <span><b style={{ color: codeColor(r.first_code) }}>{r.first_code || '—'}</b> <span style={{ color: '#888', fontSize: '0.75rem' }}>{r.first_carrier}</span></span> : '—'}</TableCell>
                  <TableCell>{r.failover_carrier ? <span><b style={{ color: codeColor(r.failover_code) }}>{r.failover_code || '—'}</b> <span style={{ color: '#888', fontSize: '0.75rem' }}>{r.failover_carrier}</span></span> : '—'}</TableCell>
                  <TableCell><b style={{ color: codeColor(r.final_code) }}>{r.final_code || '—'}</b></TableCell>
                  <TableCell><SentBy by={r.final_by} why={r.final_reason} /></TableCell>
                  <TableCell>
                    {r.answered ? <Chip size="small" color="success" label="Answered" variant="outlined" />
                      : r.failed_both ? <Chip size="small" color="error" label="Failed both" variant="outlined" />
                      : <Chip size="small" color="warning" label="Failed" variant="outlined" />}
                    {r.recovered_503 ? <Chip size="small" color="info" icon={<Replay fontSize="small" />} label="Recovered" variant="outlined" sx={{ ml: 0.5 }} /> : null}
                  </TableCell>
                </TableRow>
              ))}
              {recent.length === 0 && <TableRow><TableCell colSpan={10} align="center" sx={{ py: 4, color: 'text.secondary' }}>No matching calls.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </Box>
      </Card>
    </Box>
  );
};

// ───────────────────────────── page shell (tabs) ──────────────────────────
export const FailuresPage: React.FC = () => {
  const [tab, setTab] = useState(0);
  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1, flexWrap: 'wrap' }}>
        <MonitorHeart color="primary" sx={{ fontSize: 32 }} />
        <Box>
          <Typography variant="h5" fontWeight={700}>Failures &amp; Service Health</Typography>
          <Typography variant="body2" color="text.secondary">
            Live call health and SIP-truth diagnostics — per-route codes, 503 recovery, and live CPS per customer.
          </Typography>
        </Box>
      </Box>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Tab icon={<MonitorHeart />} iconPosition="start" label="Service Health" />
        <Tab icon={<Insights />} iconPosition="start" label="SIP-Truth Analytics" />
      </Tabs>
      {tab === 0 ? <ServiceHealthPanel /> : <SipTruthPanel />}
    </Box>
  );
};
